"use client";

import { useState, useEffect } from "react";
import { Search } from "lucide-react";
import { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
} from "@/components/ui/data-table";
import { useNavigate } from "react-router-dom";
import { get } from "../../utils/service";
import { Business } from "@/types";
import DashboardOverview from "./Overview";

interface DashboardProps {
  activeSection: string;
  setActiveSection: (section: string) => void;
  selectedCompany: string;
  setSelectedCompany: (companyId: string) => void;
  businesses: Business[];
  setBusinesses: React.Dispatch<React.SetStateAction<Business[]>>;
  loading: boolean;
  onRefresh: () => void;
}

interface EnhancedDashboardContentProps {
  loading: boolean;
  onRefresh: () => void;
}

interface DashboardData {
  stats: {
    total_leads: number;
    total_projects: number;
    active_users: number;
    total_revenue: number;
    currency?: string;
  };
  pipeline: Array<{
    stage: number;
    stage_name: string;
    leads_count: number;
    projects_count: number;
    total_count: number;
    total_value: number;
    currency?: string;
  }>;
  sales_by_month: Array<{
    month: string;
    sales: number;
    leads: number;
    conversions: number;
    currency?: string;
  }>;
  conversion_rate: {
    overall_rate: number;
    total_leads: number;
    converted: number;
  };
  revenue_by_type: Array<{
    type: string;
    revenue: number;
    count: number;
    currency?: string;
  }>;
  recent_activity: Array<unknown>;
  top_deals: Array<{
    id: string;
    name: string;
    client: string;
    value: number;
    stage: number;
    status: string;
    type: string;
    progress: number;
    currency?: string;
  }>;
  quick_stats: {
    total_pipeline_value: number;
    active_leads: number;
    active_projects: number;
    won_this_month: number;
    currency?: string;
  };
}

interface DashboardResponse {
  success: boolean;
  message: string;
  data: DashboardData;
  timestamp: string;
}

const SkeletonTable = () => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="h-10 bg-gray-200 rounded flex-1"></div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const getCurrency = (data: any, fallbackCurrency: string = "USD"): string => {
  if (data?.currency) return data.currency;
  return fallbackCurrency;
};

const ProjectLeadsTable = ({
  dashboardData,
  loading,
}: {
  dashboardData: DashboardData | null;
  loading: boolean;
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter] = useState("all");

  if (loading || !dashboardData) {
    return <SkeletonTable />;
  }

  const formatCurrency = (amount: number, currency: string = "USD") => {
    if (amount >= 1000000000) {
      return `${currency} ${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `${currency} ${(amount / 1000000).toFixed(0)}M`;
    }
    return `${currency} ${amount.toLocaleString()}`;
  };

  const leads = dashboardData.top_deals.filter(deal => {
    const matchesSearch = 
      deal.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      deal.client.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = typeFilter === "all" || deal.type === typeFilter;
    
    return matchesSearch && matchesType;
  });

  const mainCurrency =
    getCurrency(dashboardData.stats) ||
    getCurrency(dashboardData.quick_stats) ||
    "USD";

  const handleRowClick = () => {
    navigate(`/projects/all`);
  };

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="font-medium text-xs text-gray-900">
          {row.original.name}
        </div>
      ),
    },
    {
      accessorKey: "client",
      header: "Client",
      cell: ({ row }) => (
        <div className="text-xs text-gray-700">
          {row.original.client}
        </div>
      ),
    },
    {
      accessorKey: "value",
      header: "Value",
      cell: ({ row }) => {
        const currency = getCurrency(row.original, mainCurrency);
        return (
          <span className="text-xs font-semibold text-gray-900">
            {formatCurrency(row.original.value, currency)}
          </span>
        );
      },
    },
    // {
    //   accessorKey: "type",
    //   header: "Type",
    //   cell: ({ row }) => (
    //     <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
    //       {row.original.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
    //     </span>
    //   ),
    // },
    // {
    //   accessorKey: "currency",
    //   header: "Currency",
    //   cell: ({ row }) => (
    //     <div className="text-xs text-gray-700 font-medium">
    //       {row.original.currency || mainCurrency}
    //     </div>
    //   ),
    // },
  ];

  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-xs font-semibold text-gray-900">
              Recent Transactions
            </h2>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <TableProvider columns={columns} data={leads}>
          <DataTableHeader>
            {({ headerGroup }) => (
              <DataTableHeaderGroup headerGroup={headerGroup}>
                {({ header }) => <DataTableHead header={header} />}
              </DataTableHeaderGroup>
            )}
          </DataTableHeader>
          <DataTableBody>
            {({ row }) => (
              <DataTableRow
                row={row}
                className="hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => handleRowClick(row.original)}
              >
                {({ cell }) => <DataTableCell cell={cell} />}
              </DataTableRow>
            )}
          </DataTableBody>
        </TableProvider>

        {leads.length === 0 && (
          <div className="text-center py-8">
            <p className="text-xs text-gray-600">No transactions found</p>
            {searchQuery || typeFilter !== "all" && (
              <p className="text-xs text-gray-500 mt-1">Try adjusting your search or filters</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function EnhancedDashboardContent(_props: EnhancedDashboardContentProps) {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const response = await get<DashboardResponse>("/dashboard");
      if (response.success) {
        setDashboardData(response.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-4 sm:p-0 pb-8 sm:pb-8">
      <div className="w-full">
        <div className="space-y-6">
          <DashboardOverview data={dashboardData} loading={loading} />

          <ProjectLeadsTable dashboardData={dashboardData} loading={loading} />
        </div>
      </div>
    </div>
  );
}

export function Dashboard({ loading, onRefresh }: DashboardProps) {
  return (
    <div className="w-full h-full">
      <EnhancedDashboardContent loading={loading} onRefresh={onRefresh} />
    </div>
  );
}